import React, { useMemo } from 'react';
import { Breadcrumb } from 'antd';
import { HomeOutlined } from '@ant-design/icons';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { organizedCustomersService } from '../../services/customers/customersService';
import { useTenant } from '../../contexts/TenantContext';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from '../../hooks/useTranslation';

export const TenantBreadcrumb: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const tenant = useTenant();
  const { user } = useAuth();

  // Trang /customers/:uuid[/edit]: hiện TÊN khách thay vì UUID (dùng chung cache ['customer', id] với trang sửa khách).
  const customerId = location.pathname.match(/\/customers\/([0-9a-f]{8}-[0-9a-f-]{27})/i)?.[1];
  const orgSlug = tenant?.organization_slug;
  const { data: bcCustomer } = useQuery({
    queryKey: ['customer', customerId],
    enabled: !!customerId && !!orgSlug,
    retry: false,
    staleTime: 60 * 1000,
    queryFn: async () => organizedCustomersService.getCustomer(orgSlug as string, customerId as string),
  });
  const customerName = (bcCustomer as { name?: string } | undefined)?.name;

  const breadcrumbItems = useMemo(() => {
    const pathSegments = location.pathname.split('/').filter(Boolean);
    const items: Array<{
      title: React.ReactNode;
      onClick?: () => void;
    }> = [];

    // Home breadcrumb
    if (user?.user_type === 'platform_user') {
      items.push({
        title: (
          <span>
            <HomeOutlined />
            <span style={{ marginLeft: 4 }}>{t('menu.platformAdmin.platformAdmin')}</span>
          </span>
        ),
        onClick: () => navigate('/admin'),
      });
    } else if (tenant) {
      items.push({
        title: (
          <span>
            <HomeOutlined />
            <span style={{ marginLeft: 4 }}>{tenant.organization.name}</span>
          </span>
        ),
        onClick: () => navigate(`/organizations/${tenant.organization_slug}`),
      });
    }

    // Build breadcrumb from path segments
    let currentPath = '';
    for (let i = 0; i < pathSegments.length; i++) {
      const segment = pathSegments[i];
      if (!segment) continue;
      
      currentPath += `/${segment}`;
      // Snapshot theo từng vòng lặp — currentPath là biến dùng chung, mọi closure onClick bên dưới
      // đóng gói (capture) trực tiếp biến này sẽ đọc giá trị CUỐI CÙNG (URL trang hiện tại) tại thời
      // điểm click thay vì giá trị lúc push, khiến bấm bất kỳ breadcrumb nào cũng navigate về chính
      // trang đang đứng (không đổi gì, không lỗi). pathAtThisSegment chụp đúng giá trị của lượt này.
      const pathAtThisSegment = currentPath;

      // Skip if it's just the organization slug
      if (segment === tenant?.organization_slug) {
        continue;
      }

      // Skip 'organizations' and 'events' container segments when followed by a slug
      if ((segment === 'organizations' || segment === 'events') && pathSegments[i + 1]) {
        continue;
      }

      let title = segment || '';
      let clickable = true;

      // Only make non-clickable for: numeric IDs, UUIDs, and actions (create, edit)
      if (/^\d+$/.test(segment) || /^[0-9a-f-]{36}$/.test(segment) || segment === 'create' || segment === 'edit') {
        clickable = false;
      }

      // Tên đoạn tra qua i18n (menu.breadcrumb.<segment>, task 063) — trước đây ghép tên route tiếng Anh cứng.
      // Đoạn chưa có key → fallback viết hoa từ route (không để lộ key thô).
      const fallback = segment
        .split(/[-_]/)
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
      if (segment === 'organizations' && pathSegments[i + 1]) {
        // Skip this segment, we'll handle the specific org
        continue;
      }
      title = t(`menu.breadcrumb.${segment}`, { defaultValue: fallback });
      if (customerId && segment === customerId) title = customerName || '…';

      switch (segment) {
        case 'thirdparty':
          // Không có trang đích rõ ràng: /thirdparty là cha chung của 5 khu vực không liên quan
          // (suppliers/products/inventory/fulfillments/settlements), landing page mặc định (Products)
          // gây khó hiểu khi bấm từ ngữ cảnh khác (vd Suppliers) — không cho click thay vì trỏ sai chỗ.
          clickable = false;
          break;
        case 'create':
        case 'edit':
          clickable = false;
          break;
      }

      if (clickable) {
        items.push({
          title,
          onClick: () => navigate(pathAtThisSegment),
        });
      } else {
        items.push({
          title,
        });
      }
    }

    return items;
  }, [location.pathname, tenant, user, navigate, t, customerId, customerName]);

  // Don't render if we only have one item (home)
  if (breadcrumbItems.length <= 1) {
    return null;
  }

  return (
    <>
    <style>{`.tenant-bc-link:hover { color: #0A5F69 !important; text-decoration: underline; }`}</style>
    <Breadcrumb
      style={{ margin: '16px 8px 16px 16px' }}
      items={breadcrumbItems.map(item => ({
        title: item.onClick ? (
          <a
            onClick={(e) => {
              e.preventDefault();
              item.onClick?.();
            }}
            className="tenant-bc-link"
            style={{ color: '#0B6E78', cursor: 'pointer' }}
          >
            {item.title}
          </a>
        ) : (
          <span>{item.title}</span>
        ),
      }))}
    />
    </>
  );
};